import {
  Actions,
  Button,
  ButtonLink,
  Heading,
  PasswordField,
  Stack,
  Text,
  TextField,
  TextLink,
} from 'braid-design-system';
import React from 'react';

import {
  cancelPasswordReset,
  completeNewPassword,
  confirmMfa,
  confirmPasswordReset,
  formatCognitoError,
  login,
  requestPasswordReset,
} from '#src/utils/cognitoAuth';

type LoginStep =
  'login' | 'newPassword' | 'mfa' | 'forgotPassword' | 'forgotPasswordConfirm';

interface AdminLoginProps {
  onAuthenticated: () => void;
}

export const AdminLogin = ({ onAuthenticated }: AdminLoginProps) => {
  const [step, setStep] = React.useState<LoginStep>('login');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [mfaCode, setMfaCode] = React.useState('');
  const [resetCode, setResetCode] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  const resetMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const returnToLogin = () => {
    cancelPasswordReset();
    setStep('login');
    setPassword('');
    setNewPassword('');
    setMfaCode('');
    setResetCode('');
  };

  const handleLogin = async () => {
    resetMessages();
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (result.status === 'success') {
        onAuthenticated();
        return;
      }
      if (result.challenge.type === 'newPasswordRequired') {
        setStep('newPassword');
        return;
      }
      setStep('mfa');
    } catch (err) {
      setError(formatCognitoError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewPassword = async () => {
    resetMessages();
    setSubmitting(true);
    try {
      const result = await completeNewPassword(newPassword);
      if (result.status === 'success') {
        onAuthenticated();
        return;
      }
      setStep('mfa');
    } catch (err) {
      setError(formatCognitoError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleMfa = async () => {
    resetMessages();
    setSubmitting(true);
    try {
      await confirmMfa(mfaCode);
      onAuthenticated();
    } catch (err) {
      setError(formatCognitoError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPasswordRequest = async () => {
    resetMessages();
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      setStep('forgotPasswordConfirm');
    } catch (err) {
      setError(formatCognitoError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPasswordConfirm = async () => {
    resetMessages();
    setSubmitting(true);
    try {
      await confirmPasswordReset(resetCode, newPassword);
      returnToLogin();
      setSuccess('Password updated. Log in with your new password.');
    } catch (err) {
      setError(formatCognitoError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack space="medium">
      <Heading level="1">Admin</Heading>
      <Text>Log in to manage PFA site content.</Text>

      {error ? <Text tone="critical">{error}</Text> : null}
      {success ? <Text tone="positive">{success}</Text> : null}

      {step === 'login' ? (
        <Stack space="medium">
          <TextField
            label="Email"
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
            autoComplete="username"
          />
          <PasswordField
            label="Password"
            value={password}
            onChange={(event) => setPassword(event.currentTarget.value)}
            autoComplete="current-password"
          />
          <Actions>
            <Button
              onClick={() => {
                handleLogin();
              }}
              loading={submitting}
              disabled={!email.trim() || !password}
            >
              Log in
            </Button>
            <ButtonLink
              href="#"
              variant="transparent"
              onClick={(event) => {
                event.preventDefault();
                resetMessages();
                setStep('forgotPassword');
              }}
            >
              Forgot password?
            </ButtonLink>
          </Actions>
        </Stack>
      ) : null}

      {step === 'newPassword' ? (
        <Stack space="medium">
          <Text>Set a new password to finish signing in.</Text>
          <PasswordField
            label="New password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.currentTarget.value)}
            autoComplete="new-password"
          />
          <Button
            onClick={() => {
              handleNewPassword();
            }}
            loading={submitting}
            disabled={!newPassword}
          >
            Set password
          </Button>
        </Stack>
      ) : null}

      {step === 'mfa' ? (
        <Stack space="medium">
          <Text>Enter the code from your authenticator app.</Text>
          <TextField
            label="Authentication code"
            value={mfaCode}
            onChange={(event) => setMfaCode(event.currentTarget.value)}
            autoComplete="one-time-code"
          />
          <Button
            onClick={() => {
              handleMfa();
            }}
            loading={submitting}
            disabled={!mfaCode.trim()}
          >
            Verify
          </Button>
        </Stack>
      ) : null}

      {step === 'forgotPassword' ? (
        <Stack space="medium">
          <Text>
            Enter your email and we will send a verification code to reset your
            password.
          </Text>
          <TextField
            label="Email"
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
            autoComplete="username"
          />
          <Button
            onClick={() => {
              handleForgotPasswordRequest();
            }}
            loading={submitting}
            disabled={!email.trim()}
          >
            Send code
          </Button>
          <Text>
            <TextLink
              href="#"
              onClick={(event) => {
                event.preventDefault();
                returnToLogin();
              }}
            >
              Back to log in
            </TextLink>
          </Text>
        </Stack>
      ) : null}

      {step === 'forgotPasswordConfirm' ? (
        <Stack space="medium">
          <Text>
            Enter the verification code sent to {email} and choose a new
            password.
          </Text>
          <TextField
            label="Verification code"
            value={resetCode}
            onChange={(event) => setResetCode(event.currentTarget.value)}
            autoComplete="one-time-code"
          />
          <PasswordField
            label="New password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.currentTarget.value)}
            autoComplete="new-password"
          />
          <Button
            onClick={() => {
              handleForgotPasswordConfirm();
            }}
            loading={submitting}
            disabled={!resetCode.trim() || !newPassword}
          >
            Reset password
          </Button>
          <Text>
            <TextLink
              href="#"
              onClick={(event) => {
                event.preventDefault();
                returnToLogin();
              }}
            >
              Back to log in
            </TextLink>
          </Text>
        </Stack>
      ) : null}
    </Stack>
  );
};
